import React from 'react'
import { type FormikContextType, useFormikContext } from 'formik'

import { Box, Grid, FormControl, InputLabel, Select, FormHelperText, MenuItem, Typography, Switch } from '@mui/material'
import { type AssetProjectUpdateDto } from '../../../api-schema-types/assets/AssetProjectCrudDtos'
import { FormikFieldErrors } from '../../components/ui-widgets/FormikFieldErrors'
import FormikTextField from '../../components/ui-widgets/FormikTextField'
import { type EditSolarProjectComponentProps } from './EditSolarProjectForm'
import ProjectCrowdsaleStatusChip from '../../components/chips/ProjectCrowdsaleStatusChip'

const EditIdentifierStatus: React.FC<EditSolarProjectComponentProps> = ({
  project,
  overrideLockedFields
}: EditSolarProjectComponentProps) => {
  const formikProps: FormikContextType<AssetProjectUpdateDto> = useFormikContext()
  const formikErrors = new FormikFieldErrors(formikProps)

  const isIdentifierLocked = (): boolean => {
    return !overrideLockedFields && project.identifier.isLocked
  }

  const isStatusLocked = (): boolean => {
    return !overrideLockedFields && project.status.isLocked
  }

  const handlePublishedChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (!event.target.checked) {
      formikProps.setFieldValue('status.published', false)
    } else if (event.target.checked) {
      formikProps.setFieldValue('status.published', true)
    }
  }

  return (
    <>
        <Box sx={{ pb: 4 }}>
          <h3>Identifier</h3>

          <Grid container spacing={4} >

            {/* ==================================================================================================== */}

            <Grid item md={6}>
              <FormikTextField formikProps={formikProps} fieldName={'identifier.name'} label={'Project Name'} type={'text'} locked={isIdentifierLocked()}/>
            </Grid>

            <Grid item md={6}>
              <FormikTextField formikProps={formikProps} fieldName={'identifier.displayName'} label={'Display Name'} type={'text'} locked={isIdentifierLocked()}/>
            </Grid>

            {/* ==================================================================================================== */}

            <Grid item md={3}>
              <FormikTextField formikProps={formikProps} fieldName={'identifier.urlSlug'} label={'Project Url Slug'} type={'text'} locked={isIdentifierLocked()}/>
            </Grid>

            <Grid item md={3}>
              <FormikTextField formikProps={formikProps} fieldName={'identifier.projectCode'} label={'Project Code'} type={'text'} locked={isIdentifierLocked()}/>
            </Grid>

            <Grid item md={3}>
              <FormikTextField formikProps={formikProps} fieldName={'identifier.hubspotDealId'} label={'Hubspot Deal ID'} type={'text'} locked={isIdentifierLocked()}/>
            </Grid>

            <Grid item md={3}>
              <FormikTextField formikProps={formikProps} fieldName={'solarProjectId'} label={'Solar Project ID'} type={'text'} locked={true}/>
            </Grid>

          </Grid>

        </Box>

        {/* ==================================================================================================== */}

        <Box sx={{ pb: 4 }}>
          <h3>Status</h3>

          {/* ==================================================================================================== */}

          <Grid container spacing={4} >

            <Grid item md={3}>
              <FormControl sx={{ width: '100%' }}>
                <InputLabel>Project Status</InputLabel>
                <Select
                  {...formikProps.getFieldProps('status.projectStatus')}
                  label={'Project Billing Type'}
                  disabled={isStatusLocked()}
                  error={formikErrors.fieldInError('status.projectStatus')}
                >
                  {
                    project.lookups.projectStatusTypes.map((c) =>
                      <MenuItem key={c.code} value={c.code}>{c.description}</MenuItem>
                    )
                  }
                </Select>
              </FormControl>
              <FormHelperText>{formikErrors.getError('status.projectStatus')}</FormHelperText>
            </Grid>

            <Grid item md={1} >
              <Typography variant={'body2'}>Published</Typography>
              <Switch
                checked={formikProps.values.status.published}
                disabled={isStatusLocked()}
                onChange={handlePublishedChange}
                color="primary"
              />
            </Grid>

            <Grid item md={3} >
              <Typography variant={'body2'}>Crowdsale Status</Typography>
              <ProjectCrowdsaleStatusChip projectCrowdsaleStatus={project.projectCrowdsaleStatus}/>
            </Grid>

            <Grid item md={4} sx={{ mt: 3 }}></Grid>

            {/* ==================================================================================================== */}

            <Grid item md={12}>
              <FormikTextField formikProps={formikProps}
                               fieldName={'status.statusMessage'}
                               label={'Current Status Message'} type={'text'} multiline={true}
                               locked={isStatusLocked()} />
            </Grid>
          </Grid>
        </Box>

        {/* ==================================================================================================== */}

    </>
  )
}

export default EditIdentifierStatus
